import { axios } from 'api'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { AddButton } from '../ContentStructure/Components'
import moment from 'moment'
import { camelToSnake } from 'utils'
import NewMembersBarChart from './NewMembersBarChart'

const Main = styled.div`
  .flex-box {
    display: flex;
    gap: 2rem;
    flex-wrap: wrap;
  }
  .chart {
    margin: 0;
    max-width: 280px;
    width: fit-content;
    border: none;
  }
  .sub_items * {
    font-size: 95%;
  }
  .charts {
    display: flex;
    margin: 0 auto;
    width: fit-content;
    max-width: 100%;
    padding: 0;
    @media screen and (min-width: 1280px) {
      flex-direction: row;
      max-width: 50%;
    }
  }
  h4 {
    font-size: 160%;
    font-weight: 600;
  }
  .section {
    margin-top: 3rem;
  }

  ul {
    margin: 0 20px;
    li {
      font-size: 150%;
      line-height: 160%;
    }
  }
  .date-container {
    display: flex;
    align-items: center;
    gap: 3rem;
    margin-bottom: 1rem;
    flex-wrap: wrap;
  }
  .date-box {
    display: flex;
    flex-direction: column;
    .react-datepicker-wrapper {
      width: fit-content;
    }
    .error {
      font-size: 12px;
      height: 18px;
      color: red;
    }
  }
  .submit {
    min-width: 110px;
  }
  .left {
    display: flex;
    flex-direction: column;
    h4 {
      margin-top: 0;
    }
    max-width: none;
    @media screen and (min-width: 1280px) {
      max-width: 50%;
    }
  }
  .metric-title {
    font-weight: 500;
  }
  .metric-description {
    font-size: 85%;
    color: #666;
    margin-top: 2px;
    margin-bottom: 6px;
    line-height: 1.3;
  }
  .sub_items {
    margin-top: 6px;
  }
`
const MetricItem = ({ item, data }) => (
  <li>
    <div className='metric-title'>
      {item.title}: <b>{data[item.key]}</b>
    </div>

    {item.description && (
      <div className='metric-description'>{item.description}</div>
    )}

    {item.sub_items?.length > 0 && (
      <ul className='sub_items'>
        {item.sub_items.map((child) => (
          <MetricItem key={child.key} item={child} data={data} />
        ))}
      </ul>
    )}
  </li>
)

const DateBox = ({ value, setValue, label, id, error = '' }) => (
  <div className='date-box'>
    <label htmlFor={id}>{label}</label>
    <DatePicker
      maxDate={new Date()}
      id={id}
      selected={value}
      onChange={(date) => setValue(date)}
    />
    <span className='error'>{error}</span>
  </div>
)

const activityMetrics = [
  {
    title: 'Total Active Memberships',
    key: 'total_active_count',
    sub_items: [
      {
        key: 'total_active_without_aa',
        title: 'Total Members Without ALO Access'
      }
    ]
  },
  {
    title: 'Total Inactive Memberships',
    key: 'free_users'
  }
]

const signupMetrics = [
  {
    title: 'Total Sign-Ups + ALO Access Links',
    key: 'total_verifications',
    sub_items: [
      {
        title: 'Sign-Ups',
        key: 'total_awc_and_alo_linked'
      },
      {
        title: 'Links from AWC',
        key: 'existing_awc_users_linked_alo'
      },
      {
        title: 'Links from ALO Access',
        key: 'new_alo_users_verified_in_awc'
      }
    ]
  },
  {
    title: 'Total Net-New AWC Memberships',
    key: 'total_new_users',
    description:
      '*Tells us how many sign-ups and links are net-new AWC users. Calculated as Sign-Ups + Links from ALO Access. These users did not previously have active or inactive AWC membership accounts.'
  }
]

const today = new Date()
const oneMonthAgo = new Date()
oneMonthAgo.setMonth(today.getMonth() - 1)

const AWCMembershipMetricsEntry = () => {
  const [activityMetric, setActivityMetrics] = useState({})
  const [loading, setLoading] = useState(false)
  const [fromDate, setFromDate] = useState(oneMonthAgo)
  const [toDate, setToDate] = useState(today)

  const error = useMemo(
    () => moment(fromDate).isAfter(moment(toDate)),
    [fromDate, toDate]
  )

  const getData = useCallback(
    async ({ from = oneMonthAgo, to = today, first_load = false }) => {
      try {
        setLoading(true)
        const from_date = moment(from).toISOString()
        const to_date = moment(to).toISOString()
        const res = await axios.get(
          '/awc_membership_metrics/get_awc_membership_metrics',
          {
            baseURL: '/admin',
            params: {
              first_load,
              from_date,
              to_date
            }
          }
        )
        const data = res.data || {}
        const resData = camelToSnake(data)
        setActivityMetrics((state) => ({
          ...state,
          ...resData
        }))
      } catch (err) {
        console.error('Failed to fetch awc membership metric data:', err)
      } finally {
        setLoading(false)
      }
    },
    []
  )

  useEffect(() => {
    getData({ first_load: true })
  }, [getData])

  const handleSubmit = () => !error && getData({ from: fromDate, to: toDate })

  if (!activityMetric || Object.keys(activityMetric).length === 0) return null

  return (
    <Main>
      <div className='flex-box'>
        <div className='left'>
          <h4>AWC MEMBERSHIP METRICS</h4>
          <ul>
            {activityMetrics.map((item) => (
              <li key={item.key}>
                {item.title}: <b>{activityMetric[item.key]}</b>
                {item?.sub_items?.length > 0 && (
                  <ul className='sub_items'>
                    {item?.sub_items.map((si) => (
                      <li key={si.key}>
                        {si.title}: <b>{activityMetric[si.key]}</b>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div style={{ marginTop: '1.5rem' }} className='flex-box'>
        <div className='left'>
          <h4 className='section'>SIGN-UP & ALO ACCESS LINKS METRICS</h4>
          <div className='date-container'>
            <DateBox
              value={fromDate}
              setValue={setFromDate}
              label='From Date (PST)'
              id='from'
              error={error && 'Dates are out of order'}
            />
            <DateBox
              value={toDate}
              setValue={setToDate}
              label='To Date (PST)'
              id='to'
            />
            <AddButton
              onClick={handleSubmit}
              type='button'
              className='btn submit btn-primary'
              disabled={loading}
            >
              {loading ? 'Fetching...' : 'Submit'}
            </AddButton>
          </div>
          <ul>
            {signupMetrics.map((item) => (
              <MetricItem key={item.key} item={item} data={activityMetric} />
            ))}
          </ul>
        </div>
        <NewMembersBarChart data={activityMetric} />
      </div>
    </Main>
  )
}

export default AWCMembershipMetricsEntry
